/**
 * i18next bootstrap — side-effect module, imported once from `main.tsx`.
 *
 * Plugins (order matters): `resourcesToBackend` enables dynamic JSON loading,
 * `LanguageDetector` resolves + persists the user locale, `initReactI18next`
 * wires the React bindings. `escapeValue: false` is safe because React already
 * escapes interpolated output.
 *
 * The `isInitialized` guard keeps this idempotent — i18next is a singleton,
 * so an accidental second import (HMR edge cases, test harness, future SSR)
 * would otherwise re-register plugins and warn about double init.
 */

import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import resourcesToBackend from "i18next-resources-to-backend";
import { initReactI18next } from "react-i18next";

import {
  DEFAULT_NS,
  FALLBACK_LOCALE,
  LOCAL_STORAGE_LNG_KEY,
  SUPPORTED_LOCALES,
} from "./config";
import { loadResource } from "./resources";

if (!i18next.isInitialized) {
  i18next
    .use(resourcesToBackend(loadResource))
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      fallbackLng: FALLBACK_LOCALE,
      supportedLngs: [...SUPPORTED_LOCALES],
      defaultNS: DEFAULT_NS,
      // Only the shared `common` namespace is preloaded; the `auth` namespace
      // is loaded on demand by the auth screens' useTranslation("auth").
      ns: [DEFAULT_NS],
      interpolation: { escapeValue: false },
      detection: {
        order: ["localStorage", "navigator"],
        caches: ["localStorage"],
        lookupLocalStorage: LOCAL_STORAGE_LNG_KEY,
      },
    })
    .catch((error: unknown) => {
      // i18n bootstrap is fatal — no recovery path. reportError() dispatches
      // a window `error` event so global handlers (DevTools, Sentry, ...) pick
      // it up the same way as any uncaught exception. Guarded via globalThis
      // so exotic runtimes without the Web API fall back to console.error
      // instead of masking the original failure with a ReferenceError.
      if (typeof globalThis.reportError === "function") {
        globalThis.reportError(error);
      } else {
        console.error(error);
      }
    });
}
